// src/utils/catalogUi.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2 (screen).
//
// Pure helpers for the Catalog screen (no Firebase imports, so catalogUi.test.js runs them
// without the app). The SERVER decides every save (functions/utils/catalog.js); the money maths
// here mirrors it so the editor shows the price the server will store — catalogUi.test.js runs
// the same inputs through both and requires the same answers. Units and types are copies of the
// server's lists, checked the same way.

import { HIGH_LEVEL_ROLES } from './permissions';
import { csvCell } from './csvExport'; // catscreen-7a2c4e91 Stage 6 — the import template
import { isTaxCodesOn } from './taxCodes'; // taxpick4-54573069
import { savedTaxCodes } from './lineTax'; // taxpick4-54573069

// ─── switch and roles ───────────────────────────────────────────────────────
// The per-company switch is organizations/{org}.catalogScreenEnabled. Only Marcus sets it, with
// functions/scripts/setCatalogScreen.js; firestore.rules keep it server-owned.
// catroll-5c8e2a71 — Catalog is the DEFAULT: missing (every new company) = on. Only a company
// explicitly switched off (false) still sees the old Line Items page.
export const isCatalogScreenOn = (companySettings) => !!companySettings && companySettings.catalogScreenEnabled !== false;

// Owner / Admin / Manager in every spelling, plus Office Staff (= the server's catalog EDIT_ROLES and
// COST_ROLES). offcost-2e7c9a14 — founder decision 2026-10-30 (reverses catroll-5c8e2a71): Office Staff
// see the Catalog and its costs again. Sales Consultants and Field Technicians never do. Also gates the
// estimate Profit card. HIGH_LEVEL_ROLES itself is NOT widened.
export const CATALOG_ROLES = Object.freeze(['Office Staff', ...HIGH_LEVEL_ROLES]);
export const canUseCatalog = (role) => CATALOG_ROLES.includes(role);

// ─── lists (copies of functions/utils/catalog.js) ───────────────────────────
export const UNITS = Object.freeze([
    'Unit', 'Hour', 'Ft', 'SqFt',
    'Each', 'Day', 'Visit', 'LinFt', 'Metre', 'SqM', 'Yard', 'CuYd',
    'Gallon', 'Litre', 'Lb', 'Kg', 'Tonne', 'Load', 'Sheet', 'Box', 'Bag', 'Roll', 'Pack',
]);
export const TYPES = Object.freeze(['Service', 'Material', 'Part']);
export const TYPE_HELP = Object.freeze({
    Service: 'Labour or work you do. No supplier or stock.',
    Part: 'A single item you buy and install, like a breaker or valve.',
    Material: 'Bulk stuff sold by length, weight or volume, like wire or gravel.',
});
// The chips over the list, in the mockup's order.
export const TYPE_CHIPS = Object.freeze([
    { value: null, label: 'All' },
    { value: 'Service', label: 'Services' },
    { value: 'Part', label: 'Parts' },
    { value: 'Material', label: 'Materials' },
]);
export const UNGROUPED = '__ungrouped';
export const LIMITS = Object.freeze({ name: 200, description: 4000, partNumber: 80, manufacturer: 120, barcode: 64, supplierPartNumber: 80, folderName: 100, supplierName: 120, supplierText: 200, supplierNotes: 2000, money: 10000000 });

// ─── money (mirrors functions/utils/catalog.js) ─────────────────────────────
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Round to cents without the 1.005 → 1.00 float trap (same as the server). */
export function roundCents(n) {
    return Math.round((n + (n >= 0 ? Number.EPSILON : -Number.EPSILON)) * 100) / 100;
}

/** Text typed in a money box → a number, null when empty, undefined when not a number. */
export function parseMoney(text) {
    const s = String(text ?? '').replace(/[$,\s]/g, '');
    if (s === '') return null;
    if (!/^-?\d*\.?\d*$/.test(s) || s === '.' || s === '-' || s === '-.') return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
}

/** Markup and margin, 2 decimals, or null when they can't be worked out. */
export function deriveMarkup(cost, price) {
    const c = num(cost);
    const p = num(price);
    if (c === null || p === null) return { markupPct: null, marginPct: null };
    return {
        markupPct: c > 0 ? roundCents(((p - c) / c) * 100) : null,
        marginPct: p > 0 ? roundCents(((p - c) / p) * 100) : null,
    };
}

/** The price a save will store — the server's applyCostChange, step for step. */
export function previewPrice({ oldCost, oldPrice, newCost, keepMarkup, typedPrice }) {
    if (typedPrice !== undefined) return { price: typedPrice, priceMovedBy: 'typed', note: null };
    const before = num(oldCost);
    const after = num(newCost);
    if (before === after) return { price: oldPrice, priceMovedBy: 'none', note: null };
    if (!keepMarkup) return { price: oldPrice, priceMovedBy: 'none', note: null };
    if (before === null || before <= 0 || after === null || num(oldPrice) === null) {
        return { price: oldPrice, priceMovedBy: 'none', note: 'no-markup-to-keep' };
    }
    return { price: roundCents(after * (oldPrice / before)), priceMovedBy: 'markup', note: null };
}

/** Markup % typed → the price, to the cent. */
export const priceFromMarkup = (cost, markupPct) => roundCents(cost * (1 + markupPct / 100));

/** The item's cost: the default supplier's when it has suppliers, else the one entered. */
export function effectiveCost(suppliers, directCost) {
    if (Array.isArray(suppliers) && suppliers.length) {
        const d = suppliers.find((s) => s.isDefault);
        return d ? num(d.cost) : null;
    }
    return num(directCost);
}

const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const formatMoney = (n) => (num(n) === null ? '—' : MONEY.format(n));
export const formatPct = (n) => (num(n) === null ? '—' : `${Number(n.toFixed(2))}%`);
export const moneyText = (n) => (num(n) === null ? '' : roundCents(n).toFixed(2));

// ─── counts ─────────────────────────────────────────────────────────────────
const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/**
 * The line over the list. Never "0+": when the server couldn't count exactly and nothing has
 * been found yet, it says it's still looking; once something is found it says "N+".
 *   { count, countIsExact, shown, searching, filtered }
 */
export function countLine({ count, countIsExact, shown, searching, filtered }) {
    const noun = filtered ? ['match', 'matches'] : ['item', 'items'];
    if (count === null || count === undefined) return searching ? 'Searching…' : '';
    if (countIsExact) return plural(count, noun[0], noun[1]);
    const atLeast = Math.max(count, shown || 0);
    if (atLeast > 0) return `${atLeast.toLocaleString('en-US')}+ ${noun[1]}`;
    return searching ? 'Searching…' : 'Still looking — tap “Show more” to keep searching';
}

// ─── folders ────────────────────────────────────────────────────────────────
/** folders: [{ id, name, parentId, position, archived }] → { byId, children(parentId) } (live only). */
export function folderTree(folders) {
    const live = (Array.isArray(folders) ? folders : []).filter((f) => f && f.archived !== true);
    const byId = new Map(live.map((f) => [f.id, f]));
    const kids = new Map();
    for (const f of live) {
        const p = f.parentId && byId.has(f.parentId) ? f.parentId : null;
        if (!kids.has(p)) kids.set(p, []);
        kids.get(p).push(f);
    }
    const order = (a, b) => (num(a.position) ?? 0) - (num(b.position) ?? 0) || String(a.name).localeCompare(String(b.name));
    for (const list of kids.values()) list.sort(order);
    return { byId, children: (parentId) => kids.get(parentId || null) || [] };
}

/** "Plumbing › Copper" (loop-safe). */
export function folderPath(byId, id) {
    const names = [];
    const seen = new Set();
    let cur = id;
    while (cur && byId.has(cur) && !seen.has(cur) && names.length < 25) {
        seen.add(cur);
        const f = byId.get(cur);
        names.unshift(f.name);
        cur = f.parentId || null;
    }
    return names.join(' › ');
}

/** Every live folder, parents before children, with its depth (for the "Add to folder" list). */
export function flatFolders(tree) {
    const out = [];
    const walk = (parentId, depth) => {
        for (const f of tree.children(parentId)) {
            out.push({ id: f.id, name: f.name, depth });
            if (depth < 25) walk(f.id, depth + 1);
        }
    };
    walk(null, 0);
    return out;
}

// ─── price history ──────────────────────────────────────────────────────────
/** Why a history entry happened, in plain words. */
export function historyCause(e) {
    const entry = e || {};
    if (entry.kind === 'baseline') return 'Starting price when the catalog was set up';
    // catscreen-7a2c4e91 Stage 4 — bulk changes (via 'bulk', Stage 3) and imports (Stage 5 will write
    // via 'import' / 'import-undo'; labelled now so the history never shows a bare "Changed").
    if (entry.via === 'bulk' || entry.priceMovedBy === 'bulk') {
        const p = num(entry.percent);
        if (entry.action === 'changePrice' && p !== null) return `Bulk change: price ${p > 0 ? '+' : ''}${p}%`;
        if (entry.action === 'setMarkup' && p !== null) return `Bulk change: markup set to ${p}%`;
        return 'Bulk change';
    }
    if (entry.via === 'import-undo' || entry.priceMovedBy === 'import-undo') return 'Import undone';
    if (entry.via === 'import' || entry.priceMovedBy === 'import') return 'Import';
    if (entry.kind === 'created') return entry.via === 'callable' ? 'Item added' : 'Item added outside the catalog screen';
    if (entry.kind === 'deleted') return 'Item deleted';
    if (entry.via === 'direct') return 'Changed outside the catalog screen (old Line Items page, or saved from an estimate or invoice)';
    if (entry.via === 'server') return 'Changed by an import';
    if (entry.priceMovedBy === 'markup') return 'Cost changed, and Keep my markup moved the price';
    if (entry.priceMovedBy === 'typed') return 'Price changed by hand';
    if (entry.changes && entry.changes.cost && !entry.changes.price) return 'Cost changed (price kept)';
    return 'Changed';
}

/** "Price $10.00 → $12.00 · Cost $5.00 → $6.00" */
export function historyChanges(e) {
    const ch = (e && e.changes) || {};
    const part = (label, c) => {
        if (!c) return null;
        if (c.from === null || c.from === undefined) return `${label} ${formatMoney(c.to)}`;
        return `${label} ${formatMoney(c.from)} → ${formatMoney(c.to)}`;
    };
    return [part('Price', ch.price), part('Cost', ch.cost)].filter(Boolean).join(' · ');
}

// ─── bulk changes (Stage 4) — mirrors functions/utils/catalogJobs.js ────────
// catscreen-7a2c4e91. The server decides (and refuses more than 500 items without CHANGE); these
// let the screen show the same numbers first. catalogUi.test.js runs both and requires the same answers.
export const LARGE_CHANGE = 500;
export const CONFIRM_WORD = 'CHANGE';
export const PERCENT_RANGE = Object.freeze({ changePrice: [-100, 1000], setMarkup: [-100, 10000] });

/** A percent typed in a box ("-3", "−3", "+10", "12.5%") → number, null when empty, undefined when not a number. */
export function parsePercent(text) {
    const s = String(text ?? '').replace(/[\s%]/g, '').replace(/[−–—]/g, '-').replace(/^\+/, '');
    if (s === '') return null;
    if (!/^-?\d*\.?\d*$/.test(s) || s === '-' || s === '.' || s === '-.') return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
}

/** → null when the percent is fine for the action, else a plain sentence. */
export function percentProblem(action, p) {
    if (p === null || p === undefined) return 'Enter a percent, like 10 or -3.';
    const [lo, hi] = PERCENT_RANGE[action] || [-100, 1000];
    if (p < lo || p > hi) return `The percent must be between ${lo} and ${hi.toLocaleString('en-US')}.`;
    return null;
}

/** The server's planPrice, step for step. → { result: 'changed', price } | { result: 'unchanged' } | { result: 'skipped', reason } */
export function bulkPreviewPrice(action, percent, current) {
    const price = num(current.price);
    const cost = num(current.cost);
    let next;
    if (action === 'changePrice') {
        if (price === null) return { result: 'skipped', reason: 'no-price' };
        next = roundCents(price * (1 + percent / 100));
    } else {
        if (cost === null || cost <= 0) return { result: 'skipped', reason: 'no-cost' };
        next = roundCents(cost * (1 + percent / 100));
    }
    if (Object.is(next, -0)) next = 0;
    if (Math.abs(next) > LIMITS.money) return { result: 'skipped', reason: 'no-price' };
    return next === price ? { result: 'unchanged' } : { result: 'changed', price: next };
}

/** Must the user type CHANGE? Yes over 500 — and whenever the number isn't known exactly. */
export const needsTypedConfirm = (count, exact) => !exact || !(count <= LARGE_CHANGE);
export const isConfirmTyped = (text) => text === CONFIRM_WORD;

/** "2,418" or, when the server could only say "at least", "1,000+". */
export const countText = (count, exact) => (typeof count !== 'number' ? 'all' : `${count.toLocaleString('en-US')}${exact ? '' : '+'}`);

/** The orange box's first line. */
export function bigChangeLine(action, count, exact) {
    const m = countText(count, exact);
    if (action === 'changePrice' || action === 'setMarkup') return `You're about to change ${m} prices.`;
    if (action === 'archive') return `You're about to archive ${m} items.`;
    if (action === 'moveToFolder') return `You're about to move ${m} items.`;
    return `You're about to add ${m} items to a folder.`;
}

/** "Changing prices… 1,200 of 2,418" */
export function jobProgressLine(action, processed, total) {
    // catscreen-7a2c4e91 Stage 6 — imports and their undos run through the same runner.
    const verb = action === 'archive' ? 'Archiving' : action === 'moveToFolder' ? 'Moving items' : action === 'addToFolder' ? 'Adding items to the folder'
        : action === 'import' ? 'Importing' : action === 'importUndo' ? 'Undoing the import' : 'Changing prices';
    return `${verb}… ${(processed || 0).toLocaleString('en-US')} of ${(total || 0).toLocaleString('en-US')}`;
}

/** The server refused for want of CHANGE: how many items did it count? (null when that wasn't the refusal) */
export function confirmCountFromError(err) {
    const m = /This changes ([\d,]+) items\. Type CHANGE/.exec(String((err && err.message) || ''));
    return m ? Number(m[1].replace(/,/g, '')) : null;
}

// ─── pickers on estimates, jobs, invoices and templates ─────────────────────
/**
 * The catalog items a picker offers: every item that isn't archived, plus — when the line being
 * edited already points at an archived item — that one item, so the line still shows what it is.
 * → [{ ...item, pickerLabel }]
 */
export function pickerItems(services, selectedId) {
    const list = Array.isArray(services) ? services : [];
    return list
        .filter((s) => s && (s.archived !== true || (selectedId && s.id === selectedId)))
        .map((s) => (s.archived === true ? { ...s, pickerLabel: `${s.name} (archived)` } : { ...s, pickerLabel: s.name }));
}

// ─── the editor's save ──────────────────────────────────────────────────────
const trimOrNull = (s) => { const t = String(s ?? '').trim(); return t || null; };

/**
 * Checks the editor's draft before it goes to the server. → null, or a plain sentence.
 *   draft: { name, priceText, costText, markupText, suppliers: [{ supplierId, costText }] }
 */
export function findDraftProblem(draft, { creating }) {
    if (!String(draft.name || '').trim()) return 'Please give the item a name.';
    if (String(draft.name).trim().length > LIMITS.name) return `The name can be up to ${LIMITS.name} characters.`;
    const price = parseMoney(draft.priceText);
    if (price === undefined) return 'The price must be a number, like 12.50.';
    if (creating && price === null) return 'Please enter a price. It can be 0.';
    if (price !== null && Math.abs(price) > LIMITS.money) return 'That price is too large.';
    if (!draft.suppliers || !draft.suppliers.length) {
        const cost = parseMoney(draft.costText);
        if (cost === undefined) return 'The cost must be a number, like 8.25.';
        if (cost !== null && cost < 0) return "The cost can't be negative.";
    }
    for (const s of draft.suppliers || []) {
        const c = parseMoney(s.costText);
        if (c === undefined || c === null) return `Please enter a cost for ${s.name || 'each supplier'}.`;
        if (c < 0) return "A supplier's cost can't be negative.";
    }
    return null;
}

/** What the editor shows as the cost right now (the default supplier's, or the one typed). */
export function draftCost(draft) {
    if (draft.suppliers && draft.suppliers.length) {
        const d = draft.suppliers.find((s) => s.isDefault);
        const c = d ? parseMoney(d.costText) : null;
        return typeof c === 'number' ? c : null;
    }
    const c = parseMoney(draft.costText);
    return typeof c === 'number' ? c : null;
}

// ─── the item's Settings › Taxes code (taxpick4-54573069, tax codes on every line S4) ─────
// A company with Settings › Taxes on and no QuickBooks gives an item an optional code
// (salesTaxCodeId): lines added from the catalog start with it; blank = the company default.
// QuickBooks companies keep their QuickBooks tax code box exactly as before. The server
// (catalog.salesTaxCodeRefusal) re-checks every code that is set.
export const ITEM_TAX_CODE_HINT = 'Blank uses your company default. Lines added from the catalog start with the item’s code. Owner, Admin, Manager and Office Staff can set it.';

/** The codes the item editor offers, or null = no "Tax code" box (Taxes off, QuickBooks, still loading, none saved). */
export function catalogSalesTaxCodes(orgId, taxCodesDoc, qbConnected) {
    if (qbConnected === true || taxCodesDoc === undefined || !isTaxCodesOn(orgId, taxCodesDoc)) return null;
    const codes = (savedTaxCodes(taxCodesDoc) || []).filter((c) => c && typeof c.id === 'string' && c.id !== '' && Array.isArray(c.parts) && c.parts.length > 0);
    return codes.length ? codes : null;
}

/** The box's choices: "Use company default", the codes in use (the default marked), and the item's own stopped/missing code. */
export function itemTaxCodeChoices(codes, currentId) {
    const list = Array.isArray(codes) ? codes : [];
    const out = [{ value: '', label: 'Use company default' }];
    list.filter((c) => c.stopped !== true).forEach((c) => out.push({ value: c.id, label: c.isDefault === true ? `${c.name} (default)` : c.name }));
    if (currentId && !out.some((o) => o.value === currentId)) {
        const own = list.find((c) => c.id === currentId);
        out.push({ value: currentId, label: own ? `${own.name} (no longer used)` : 'Current code (no longer in Settings › Taxes)' });
    }
    return out;
}

/**
 * The saveCatalogItem input for a draft, and whether the folders changed (existing items change
 * folders through setCatalogItemFolders after the save).
 *   original: { fields, price, cost, keepMarkup, suppliers, folderIds } as loaded (null when creating)
 *   priceTouched: the user typed a price or a markup in this edit
 *   showTax: the company uses QuickBooks (the tax box is shown)
 *   salesTax: the item's Settings › Taxes "Tax code" box is shown (taxpick4-54573069) — only then is
 *             salesTaxCodeId sent, so every other company's saves are exactly as before
 */
export function buildSaveInput(draft, original, { creating, priceTouched, showTax, salesTax }) {
    const fields = {
        name: String(draft.name).trim(),
        type: draft.type,
        unit: draft.unit,
        description: String(draft.description ?? '').trim(),
        partNumber: trimOrNull(draft.partNumber),
        manufacturer: trimOrNull(draft.manufacturer),
        barcode: trimOrNull(draft.barcode),
    };
    if (showTax) fields.taxCodeId = trimOrNull(draft.taxCodeId);
    if (salesTax) fields.salesTaxCodeId = trimOrNull(draft.salesTaxCodeId); // taxpick4-54573069
    const input = {};
    if (creating) {
        input.fields = fields;
    } else {
        const changed = {};
        for (const [k, v] of Object.entries(fields)) {
            const was = original.fields[k] === undefined ? null : original.fields[k];
            const norm = (x) => (x === '' ? null : x);
            if (norm(v) !== norm(was)) changed[k] = v;
        }
        if (Object.keys(changed).length) input.fields = changed;
    }
    const suppliers = (draft.suppliers || []).map((s) => ({
        supplierId: s.supplierId,
        supplierPartNumber: trimOrNull(s.supplierPartNumber),
        cost: roundCents(parseMoney(s.costText)),
        isDefault: s.isDefault === true,
    }));
    const sameSuppliers = !creating && JSON.stringify(suppliers) === JSON.stringify((original.suppliers || []).map((s) => ({
        supplierId: s.supplierId, supplierPartNumber: s.supplierPartNumber || null, cost: s.cost, isDefault: s.isDefault === true,
    })));
    if (creating ? suppliers.length > 0 : !sameSuppliers) input.suppliers = suppliers;
    if (!suppliers.length) {
        const cost = parseMoney(draft.costText);
        const c = cost === null ? null : roundCents(cost);
        if (creating ? c !== null : c !== original.cost && !(c === null && original.cost === null)) input.cost = c;
    }
    if (creating || draft.keepMarkup !== original.keepMarkup) input.keepMarkup = draft.keepMarkup;
    const price = parseMoney(draft.priceText);
    if (creating || priceTouched) input.price = roundCents(price);
    const folderIds = [...new Set(draft.folderIds || [])].sort();
    const foldersChanged = !creating && JSON.stringify(folderIds) !== JSON.stringify([...(original.folderIds || [])].sort());
    if (creating && folderIds.length) input.folderIds = folderIds;
    return { input, folderIds, foldersChanged };
}

// ─── import (Stage 6) — the screen for functions/utils/catalogImport.js ─────
// catscreen-7a2c4e91. The SERVER decides everything (planCatalogImport checks and matches every row;
// confirmCatalogImport enforces the big-change tick and CHANGE). These only shape what the screen
// shows. catalogUi.test.js checks the columns against the server's.

// The export's columns, Spatchy ID first — exactly what the importer reads (catalogJobs.EXPORT_COLUMNS).
export const IMPORT_COLUMNS = Object.freeze([
    'Spatchy ID', 'Name', 'Type', 'Unit', 'Description', 'Price', 'Cost', 'Keep my markup',
    'Your part #', 'Manufacturer', 'Barcode', 'QuickBooks tax code ID',
    'Default supplier', 'Supplier part #', 'Supplier cost', 'Folders', 'Archived',
]);
export const IMPORT_MAX_BYTES = 10 * 1024 * 1024; // = the server's MAX_CHARS
export const IMPORT_MAX_ROWS = 75000; // = the server's MAX_ROWS
export const IMPORT_MATCH_LINE = 'matched to your catalog by the Spatchy ID column, then your part #, then supplier part #, then name';

// The shared spreadsheet formula guard (utils/csvExport.js, s9csv-4e1b7c28) quotes every cell.
const csvQuote = csvCell;

/** "Download template": the columns and one example row, as Excel likes it (BOM, CRLF). */
export function importTemplateCsv() {
    const example = {
        Name: 'Example: 1/2" copper elbow (delete this row)', Type: 'Part', Unit: 'Each',
        Description: 'Copper sweat elbow, 90°', Price: '13.00', Cost: '10.00', 'Keep my markup': 'Yes',
        'Your part #': 'CE-12', Manufacturer: 'Acme',
    };
    const lines = [IMPORT_COLUMNS.map(csvQuote).join(','), IMPORT_COLUMNS.map((h) => csvQuote(example[h] || '')).join(',')];
    return `﻿${lines.join('\r\n')}\r\n`;
}

/** The file someone picked → null when it can be read, else what to do instead. */
export function importFileProblem(fileName, size) {
    const n = String(fileName || '').toLowerCase();
    if (/\.(xlsx|xlsm|xls|ods|numbers)$/.test(n)) return 'Save it as CSV from Excel (File → Save As → CSV) and choose it again.';
    if (!/\.(csv|txt)$/.test(n)) return 'Choose a CSV file (it ends in .csv). In Excel or Google Sheets, save or download the sheet as CSV.';
    if (typeof size === 'number' && size > IMPORT_MAX_BYTES) return 'This file is bigger than 10 MB. Split it into smaller files and import each one.';
    if (size === 0) return 'This file is empty.';
    return null;
}

/**
 * papaparse's rows (every line kept, so row numbers match the file) → what planCatalogImport takes.
 * Only empty lines at the very END are dropped (a file's last line break). → { headers, rows, rowCount, problem }
 */
export function prepareImportRows(data) {
    const all = Array.isArray(data) ? data.map((r) => (Array.isArray(r) ? r.map((c) => (c == null ? '' : String(c))) : [])) : [];
    const blankLine = (r) => r.every((c) => c.trim() === '');
    while (all.length && blankLine(all[all.length - 1])) all.pop();
    if (!all.length) return { headers: [], rows: [], rowCount: 0, problem: 'This file is empty.' };
    const headers = all[0];
    const rows = all.slice(1);
    const rowCount = rows.filter((r) => !blankLine(r)).length;
    if (headers.length === 1 && /;/.test(headers[0])) {
        return { headers, rows, rowCount, problem: 'This file is separated by semicolons. Save it as "CSV (comma delimited)" and choose it again.' };
    }
    if (!rowCount) return { headers, rows, rowCount, problem: 'This file has headings but no rows.' };
    if (rows.length > IMPORT_MAX_ROWS) return { headers, rows, rowCount, problem: `This file has ${rows.length.toLocaleString('en-US')} rows. Import up to ${IMPORT_MAX_ROWS.toLocaleString('en-US')} at a time.` };
    return { headers, rows, rowCount, problem: null };
}

// The chips over the preview, in the mockup's order. "Need fixing" covers bad rows AND unclear matches.
export const IMPORT_CHIPS = Object.freeze(['all', 'update', 'new', 'big', 'fix', 'unchanged']);
const chipTest = {
    all: () => true,
    update: (r) => r.status === 'update',
    new: (r) => r.status === 'new',
    big: (r) => r.big === true,
    fix: (r) => r.needsFixing === true || r.status === 'fix' || r.status === 'bad',
    unchanged: (r) => r.status === 'unchanged',
};
export const filterImportRows = (rows, chip) => (Array.isArray(rows) ? rows.filter(chipTest[chip] || chipTest.all) : []);

/** → [{ key, label }] e.g. "All 2,004", "1,962 updates", "9 over 20%". */
export function importChips(rows) {
    const n = (k) => filterImportRows(rows, k).length;
    const f = (x) => x.toLocaleString('en-US');
    const pl = (x, one, many) => `${f(x)} ${x === 1 ? one : many}`;
    return [
        { key: 'all', label: `All ${f(n('all'))}` },
        { key: 'update', label: pl(n('update'), 'update', 'updates') },
        { key: 'new', label: pl(n('new'), 'new item', 'new items') },
        { key: 'big', label: `${f(n('big'))} over 20%` },
        { key: 'fix', label: `${f(n('fix'))} need fixing` },
        { key: 'unchanged', label: `${f(n('unchanged'))} unchanged` },
    ];
}

/** The status badge: { label, tone } — tone: update | new | big | fix | same. */
export function importBadge(r) {
    if (r.needsFixing || r.status === 'fix' || r.status === 'bad') return { label: 'Needs fixing', tone: 'fix' };
    if (r.status === 'new') return { label: 'New item', tone: 'new' };
    if (r.status === 'unchanged') return { label: 'Unchanged', tone: 'same' };
    if (r.big) return { label: 'Over 20%', tone: 'big' };
    return { label: 'Update', tone: 'update' };
}

const NAME_DIFFERS = 'Name in the file differs — not changed.';
const pctMove = (from, to) => {
    const a = num(from);
    const b = num(to);
    if (a === null || b === null || a === 0) return null;
    return Math.round(((b - a) / Math.abs(a)) * 100);
};

/**
 * "What will change" for one preview row → { text, notes } (notes: grey lines under it; the
 * name note is kept apart as nameNote for the Item column).
 */
export function describeImportRow(r) {
    const notes = (Array.isArray(r.notes) ? r.notes : []).filter((x) => x !== NAME_DIFFERS);
    const nameNote = Array.isArray(r.notes) && r.notes.includes(NAME_DIFFERS) ? NAME_DIFFERS : null;
    if (r.status === 'fix' || r.status === 'bad' || r.needsFixing) return { text: r.reason || 'Needs fixing.', notes, nameNote };
    if (r.status === 'unchanged') return { text: 'No change', notes, nameNote };
    const ch = r.changes || {};
    if (r.status === 'new') {
        const price = ch.price ? formatMoney(ch.price[1]) : '—';
        const cost = ch.cost && num(ch.cost[1]) !== null ? ` · cost ${formatMoney(ch.cost[1])}` : '';
        return { text: `New item · ${price}${cost}`, notes, nameNote };
    }
    const parts = [];
    const move = (label, pair, extra) => {
        const p = pctMove(pair[0], pair[1]);
        const big = r.big && p !== null && Math.abs(p) > 20 ? ` (${p > 0 ? '+' : ''}${p}%)` : '';
        return `${label} ${formatMoney(pair[0])} → ${formatMoney(pair[1])}${big}${extra || ''}`;
    };
    if (ch.cost) parts.push(move('Cost', ch.cost));
    if (ch.price) parts.push(move('Price', ch.price, r.priceMovedBy === 'markup' ? ' (Keep my markup)' : ''));
    if (ch.keepMarkup) parts.push(`Keep my markup ${ch.keepMarkup[1] ? 'on' : 'off'}`);
    const others = (Array.isArray(r.changedFields) ? r.changedFields : []).filter((f) => !['Price', 'Cost', 'Keep my markup'].includes(f));
    if (others.length) parts.push(`${others.join(', ')} changed`);
    return { text: parts.join(' · ') || 'Changes', notes, nameNote };
}

/**
 * May Import be pressed? → { ok, reason } — reason is the button's own words when it can't.
 *   plan: planCatalogImport's answer; bigOk: the tick; confirmText: what was typed; nowMs: Date.now()
 */
export function importGate(plan, { bigOk, confirmText, nowMs }) {
    if (!plan || !plan.jobId || !(plan.toImport > 0)) return { ok: false, reason: 'Nothing to import' };
    if (typeof plan.expiresAtMs === 'number' && nowMs > plan.expiresAtMs) return { ok: false, reason: 'Choose the file again' };
    if (plan.needsBigTick && !bigOk) return { ok: false, reason: 'Check the big changes first' };
    if (plan.needsConfirmWord && confirmText !== CONFIRM_WORD) return { ok: false, reason: 'Type CHANGE to confirm' };
    return { ok: true, reason: null };
}

export const IMPORT_EXPIRED = 'This preview is more than a day old. Choose the file again.';
/** Did the server refuse because the preview is too old? */
export const isImportExpiredError = (err) => /more than a day old/i.test(String((err && err.message) || ''));

/** One Import history row's status: Imported | Undone | Partly undone | Not finished | Undo not finished. */
export function importHistoryStatus(entry) {
    const e = entry || {};
    if (e.status !== 'done') return 'Not finished';
    if (!e.undo) return 'Imported';
    if (e.undo.status !== 'done') return 'Undo not finished';
    return (e.undo.counts && e.undo.counts.skipped > 0) ? 'Partly undone' : 'Undone';
}

/** "6 changed · 2 new · 1 left alone" for an import (new comes from its preview). */
export function importCountsLine(entry) {
    const c = (entry && entry.counts) || {};
    const s = (entry && entry.summary) || {};
    const f = (x) => (x || 0).toLocaleString('en-US');
    const created = Math.min(s.new || 0, c.changed || 0);
    const parts = [`${f((c.changed || 0) - created)} updated`];
    if (created) parts.push(`${f(created)} new`);
    if (c.skipped) parts.push(`${f(c.skipped)} left alone`);
    return parts.join(' · ');
}
